import {
  ANCHOR_SPEC_VERSION,
  CATEGORIES,
  FACE_ANCHOR_ORIGINS,
  FOOT_ANCHOR_ORIGINS,
  HAND_ANCHOR_ORIGINS,
  defaultAnchorSpec,
  type AnchorSpec,
  type Category,
  type EarringHints,
  type RingHints,
  type MaterialOverride,
  type OccluderType,
  type Variant,
} from './schema'
import type { Vec3 } from '../math/vec3'

export class AnchorSpecError extends Error {
  constructor(public readonly path: string, message: string) {
    super(`anchor.json ${path}: ${message}`)
    this.name = 'AnchorSpecError'
  }
}

const OCCLUDER_TYPES: readonly OccluderType[] = ['none', 'face', 'head', 'face+head', 'hand', 'wrist', 'foot']
const ALL_ORIGINS: readonly string[] = [...FACE_ANCHOR_ORIGINS, ...HAND_ANCHOR_ORIGINS, ...FOOT_ANCHOR_ORIGINS]

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}
function num(v: unknown, path: string, fallback?: number): number {
  if (v === undefined && fallback !== undefined) return fallback
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new AnchorSpecError(path, 'expected a finite number')
  return v
}
function vec3Of(v: unknown, path: string, fallback?: Vec3): Vec3 {
  if (v === undefined && fallback) return [...fallback]
  if (!Array.isArray(v) || v.length !== 3) throw new AnchorSpecError(path, 'expected [x, y, z]')
  return [num(v[0], `${path}[0]`), num(v[1], `${path}[1]`), num(v[2], `${path}[2]`)]
}
function str(v: unknown, path: string): string {
  if (typeof v !== 'string' || v.length === 0) throw new AnchorSpecError(path, 'expected a non-empty string')
  return v
}
function optStr(v: unknown, path: string): string | undefined {
  if (v === undefined) return undefined
  return str(v, path)
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], path: string): T {
  if (typeof v !== 'string' || !allowed.includes(v as T))
    throw new AnchorSpecError(path, `expected one of ${allowed.join(', ')}`)
  return v as T
}

function parseMaterial(v: unknown, path: string): MaterialOverride {
  if (!isRecord(v)) throw new AnchorSpecError(path, 'expected an object')
  const m: MaterialOverride = {}
  if (v.color !== undefined) {
    const c = str(v.color, `${path}.color`)
    if (!/^#[0-9a-fA-F]{6}$/.test(c)) throw new AnchorSpecError(`${path}.color`, 'expected "#RRGGBB"')
    m.color = c
  }
  if (v.metalness !== undefined) m.metalness = num(v.metalness, `${path}.metalness`)
  if (v.roughness !== undefined) m.roughness = num(v.roughness, `${path}.roughness`)
  if (v.opacity !== undefined) m.opacity = num(v.opacity, `${path}.opacity`)
  if (v.map !== undefined) m.map = str(v.map, `${path}.map`)
  return m
}

function parseVariant(v: unknown, path: string): Variant {
  if (!isRecord(v)) throw new AnchorSpecError(path, 'expected an object')
  const out: Variant = { id: str(v.id, `${path}.id`), name: str(v.name, `${path}.name`) }
  const glbUrl = optStr(v.glbUrl, `${path}.glbUrl`)
  if (glbUrl) out.glbUrl = glbUrl
  const thumb = optStr(v.thumbnailUrl, `${path}.thumbnailUrl`)
  if (thumb) out.thumbnailUrl = thumb
  if (v.materials !== undefined) {
    if (!isRecord(v.materials)) throw new AnchorSpecError(`${path}.materials`, 'expected an object')
    out.materials = {}
    for (const [k, mv] of Object.entries(v.materials)) out.materials[k] = parseMaterial(mv, `${path}.materials.${k}`)
  }
  return out
}

/**
 * Parse + validate an `anchor.json` document. Missing optional fields get category defaults.
 * Throws `AnchorSpecError` with a JSON path on the first problem.
 */
export function parseAnchorSpec(input: unknown): AnchorSpec {
  if (!isRecord(input)) throw new AnchorSpecError('$', 'expected an object')
  const version = input.version === undefined ? ANCHOR_SPEC_VERSION : input.version
  if (version !== ANCHOR_SPEC_VERSION) throw new AnchorSpecError('$.version', `unsupported version ${String(version)}`)
  const category = oneOf<Category>(input.category, CATEGORIES, '$.category')
  const d = defaultAnchorSpec(category)

  const spec: AnchorSpec = {
    version: ANCHOR_SPEC_VERSION,
    category,
    origin: input.origin === undefined ? d.origin : (oneOf(input.origin, ALL_ORIGINS, '$.origin') as AnchorSpec['origin']),
    offset: vec3Of(input.offset, '$.offset', d.offset),
    rotation: vec3Of(input.rotation, '$.rotation', d.rotation),
    scale: num(input.scale, '$.scale', d.scale),
    unitScale: num(input.unitScale, '$.unitScale', d.unitScale),
    occluder: { ...d.occluder },
    variants: [],
    refineLandmark: d.refineLandmark ?? null,
  }
  if (spec.scale <= 0) throw new AnchorSpecError('$.scale', 'must be > 0')
  if (spec.unitScale <= 0) throw new AnchorSpecError('$.unitScale', 'must be > 0')

  if (input.occluder !== undefined) {
    if (!isRecord(input.occluder)) throw new AnchorSpecError('$.occluder', 'expected an object')
    spec.occluder = {
      type: input.occluder.type === undefined ? d.occluder.type : oneOf(input.occluder.type, OCCLUDER_TYPES, '$.occluder.type'),
      depthOffset: num(input.occluder.depthOffset, '$.occluder.depthOffset', 0),
      scale: num(input.occluder.scale, '$.occluder.scale', 1),
    }
    if (typeof input.occluder.debug === 'boolean') spec.occluder.debug = input.occluder.debug
  }

  if (input.variants !== undefined) {
    if (!Array.isArray(input.variants)) throw new AnchorSpecError('$.variants', 'expected an array')
    spec.variants = input.variants.map((v, i) => parseVariant(v, `$.variants[${i}]`))
    const ids = new Set<string>()
    for (const v of spec.variants) {
      if (ids.has(v.id)) throw new AnchorSpecError('$.variants', `duplicate variant id "${v.id}"`)
      ids.add(v.id)
    }
  }

  if (input.refineLandmark !== undefined && input.refineLandmark !== null) {
    const idx = num(input.refineLandmark, '$.refineLandmark')
    if (!Number.isInteger(idx) || idx < 0) throw new AnchorSpecError('$.refineLandmark', 'expected a landmark index ≥ 0')
    spec.refineLandmark = idx
  }

  if (input.glasses !== undefined) {
    if (!isRecord(input.glasses)) throw new AnchorSpecError('$.glasses', 'expected an object')
    const g = input.glasses
    spec.glasses = { ...(d.glasses ?? {}) }
    if (g.templeNodes !== undefined) {
      if (!Array.isArray(g.templeNodes) || g.templeNodes.length !== 2)
        throw new AnchorSpecError('$.glasses.templeNodes', 'expected [leftNodeName, rightNodeName]')
      spec.glasses.templeNodes = [str(g.templeNodes[0], '$.glasses.templeNodes[0]'), str(g.templeNodes[1], '$.glasses.templeNodes[1]')]
    }
    if (g.lensNodes !== undefined) {
      if (!Array.isArray(g.lensNodes)) throw new AnchorSpecError('$.glasses.lensNodes', 'expected an array of node names')
      spec.glasses.lensNodes = g.lensNodes.map((n, i) => str(n, `$.glasses.lensNodes[${i}]`))
    }
    if (g.lensTint !== undefined) spec.glasses.lensTint = str(g.lensTint, '$.glasses.lensTint')
    if (g.lensOpacity !== undefined) spec.glasses.lensOpacity = num(g.lensOpacity, '$.glasses.lensOpacity')
    if (g.autoFitTemples !== undefined) {
      if (typeof g.autoFitTemples !== 'boolean') throw new AnchorSpecError('$.glasses.autoFitTemples', 'expected boolean')
      spec.glasses.autoFitTemples = g.autoFitTemples
    }
  } else if (d.glasses) {
    spec.glasses = { ...d.glasses }
  }

  if (input.earring !== undefined) {
    if (!isRecord(input.earring)) throw new AnchorSpecError('$.earring', 'expected an object')
    const e = input.earring
    const hints: EarringHints = { ...(d.earring ?? {}) }
    if (e.dangle !== undefined) {
      if (typeof e.dangle !== 'boolean') throw new AnchorSpecError('$.earring.dangle', 'expected boolean')
      hints.dangle = e.dangle
    }
    if (e.bothEars !== undefined) {
      if (typeof e.bothEars !== 'boolean') throw new AnchorSpecError('$.earring.bothEars', 'expected boolean')
      hints.bothEars = e.bothEars
    }
    if (e.lengthCm !== undefined) {
      const v = num(e.lengthCm, '$.earring.lengthCm')
      if (v <= 0) throw new AnchorSpecError('$.earring.lengthCm', 'must be > 0')
      hints.lengthCm = v
    }
    if (e.damping !== undefined) hints.damping = num(e.damping, '$.earring.damping')
    spec.earring = hints
  } else if (d.earring) {
    spec.earring = { ...d.earring }
  }

  if (input.ring !== undefined) {
    if (!isRecord(input.ring)) throw new AnchorSpecError('$.ring', 'expected an object')
    const hints: RingHints = { ...(d.ring ?? {}) }
    if (input.ring.finger !== undefined)
      hints.finger = oneOf(input.ring.finger, ['thumb', 'index', 'middle', 'ring', 'pinky'] as const, '$.ring.finger')
    if (input.ring.hand !== undefined) hints.hand = oneOf(input.ring.hand, ['left', 'right', 'any'] as const, '$.ring.hand')
    spec.ring = hints
  } else if (d.ring) {
    spec.ring = { ...d.ring }
  }

  if (input.meta !== undefined) {
    if (!isRecord(input.meta)) throw new AnchorSpecError('$.meta', 'expected an object')
    spec.meta = { ...input.meta } as AnchorSpec['meta']
  }
  return spec
}

/** Serialise a spec to the JSON that goes next to model.glb (stable key order). */
export function serializeAnchorSpec(spec: AnchorSpec): string {
  return JSON.stringify(spec, null, 2)
}
